import { LOGIN_TYPES } from "./types";
import {
  FETCH_MESSAGES_REQUEST,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_FAILURE,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_FAILURE,
  MARK_MESSAGE_READ_REQUEST,
  MARK_MESSAGE_READ_SUCCESS,
  MARK_MESSAGE_READ_FAILURE
} from './types';
import axios from 'axios';
import apiClient from '../../utils/apiConfig';

/**
 * Preload an avatar image into the browser's HTTP cache.
 * Called at login/profile-fetch time so the image is ready
 * before any drawer or card ever renders.
 * "Video game loading screen" approach — load the asset early, use it later.
 */
const preloadAvatarImage = (url) => {
  if (url && typeof url === 'string' && url.startsWith('https://') && !url.includes('://undefined')) {
    const img = new Image();
    img.src = url;
    // Also preload the optimized sizes that BurrowAvatar will use
    const imgMed = new Image();
    imgMed.src = url.includes('?') ? url + '&class=avatarmed' : url + '?class=avatarmed';
    const imgLarge = new Image();
    imgLarge.src = url.includes('?') ? url + '&class=avatarlarge' : url + '?class=avatarlarge';
    console.log('[Avatar Preload] Caching avatar:', url);
  }
};

// Auth actions
export const login = (params) => {
  const email = params[0].burrowerId || params[0].email;
  const userResponse = params[3]; // Get the user data from the response
  
  // Create user object with role and other data from API response
  const userData = { 
    role: userResponse?.role,
    accountType: userResponse?.accountType,
    burrowerId: userResponse?.burrowerId
  };
  
  // Store complete user data in localStorage for persistence
  localStorage.setItem('burrowUserData', JSON.stringify(userData));
  
  return {
    type: LOGIN_TYPES.LOGIN,
    payload: {
      email: email,
      password: params[0].password,
      token: params[2],
      user: userData,
    },
  };
};

export const tokenisValid = (params) => {
  return {
    type: LOGIN_TYPES.VALIDITY_TOKEN,
    payload: params,
  };
};

export const logout = () => (dispatch) => {
  // Clear all auth-related items from localStorage
  localStorage.removeItem("burrowtoken");
  localStorage.removeItem("loginLock");
  localStorage.removeItem("burrowUserData");
  localStorage.removeItem("adminSettings");
  localStorage.removeItem("isAdmin"); // Also remove isAdmin from localStorage
  localStorage.removeItem("adminLevel"); // Remove admin level
  localStorage.removeItem("hasAdminAccess"); // Phase 1C: Remove sidecar flag

  // Dispatch logout action to reset auth state
  dispatch({
    type: LOGIN_TYPES.LOGOUT,
  });
  
  // Force a page reload which will reset all Redux state
  window.location.reload();
};

// Define the removeuser function that was previously undefined
export const removeuser = () => {
  return {
    type: LOGIN_TYPES.LOGOUT,
  };
};

// Add the missing setUserData action creator
export const setUserData = (userData, isAdmin) => {
  return {
    type: LOGIN_TYPES.SET_USER_DATA,
    payload: {
      user: userData,
      isAdmin: isAdmin
    }
  };
};

export const registerUser = (params) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_TYPES.REGISTER_REQUEST });

    const response = await apiClient.post('/api/auth/register', params);

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.REGISTER_SUCCESS,
        payload: response.data,
      });
      return { 
        success: true,
        message: 'Registration successful',
        ...response.data 
      };
    } else {
      throw new Error(response.data.message || 'Registration failed');
    }
  } catch (error) {
    dispatch({
      type: LOGIN_TYPES.REGISTER_FAILURE,
      payload: error.message,
    });

    // Return structured error response
    return { 
      success: false,
      message: error.response?.data?.message || error.message,
      fieldErrors: error.response?.data?.fieldErrors,
      error: error.message 
    };
  }
};

// Modify getusertoken to properly handle the login response
export const getusertoken = (params) => async (dispatch) => {
  try {
    const { burrowerId, email, password } = params.userlogin;

    // Check lock status first but don't enforce lockout
    const lockData = localStorage.getItem("loginLock");
    if (lockData) {
      const { attempts } = JSON.parse(lockData);
      // Track attempts but don't enforce lockout
      if (attempts && attempts >= 5) {
        console.log("Too many attempts, but continuing anyway");
      }
    }

    console.log("Attempting login with:", { burrowerId: burrowerId || email });
    
    // Make login request
    const response = await apiClient.post('/api/auth/login', {
        burrowerId: burrowerId || email,
        password,
      });

    console.log("Login response:", response.data);

    if (response.data && response.data.success && response.data.token) {
      // Check if user must change password (admin-forced reset)
      if (response.data.mustChangePassword) {
        // Store token temporarily for the password change flow
        const tempResetToken = response.data.token;
        const tempBurrowerId = response.data.user?.burrowerId || burrowerId || email;
        
        // Don't complete login — redirect to password change
        if (params.onClose) params.onClose();
        window.location.href = `/forgot-password?mustChange=true&burrowerId=${encodeURIComponent(tempBurrowerId)}`;
        
        return { success: false, mustChangePassword: true, message: 'You must change your password before logging in.' };
      }

      const token = response.data.token;
      const userData = response.data.user;

      // Explicitly check if user is admin based on the response
      const isAdmin = userData?.role === 'admin' || userData?.accountType === 'admin';
      
      console.log("User role/type:", userData?.role, userData?.accountType);
      console.log("Is admin:", isAdmin);

      console.log("Login successful, updating state with:", userData);
      
      // Pre-cache avatar image so it's ready before any drawer/card renders
      if (userData?.userAvatar) {
        preloadAvatarImage(userData.userAvatar);
      }
      
      // Store token and user data including profile fields
      localStorage.setItem("burrowtoken", token);
      localStorage.setItem("burrowUserData", JSON.stringify(userData));
      localStorage.setItem("isAdmin", isAdmin.toString());
      // Phase 1C: Store hasAdminAccess for sidecar model
      if (userData?.hasAdminAccess) {
        localStorage.setItem("hasAdminAccess", "true");
      } else {
        localStorage.removeItem("hasAdminAccess");
      }
      if (userData?.adminLevel) {
        localStorage.setItem("adminLevel", userData.adminLevel);
      } else {
        localStorage.removeItem("adminLevel");
      }
      localStorage.removeItem("loginLock");

      // Reset login attempts
      dispatch({
        type: LOGIN_TYPES.LOGIN_ATTEMPT_RESET,
      });

      // First dispatch a REFRESH_AUTH_STATE action to ensure we reset any stale state
      dispatch({ type: LOGIN_TYPES.REFRESH_AUTH_STATE });
      
      // Dispatch login action with user data and admin flag
      dispatch({
        type: LOGIN_TYPES.LOGIN,
        payload: {
          email: burrowerId || email,
          password,
          token,
          user: userData,
          isAdmin: isAdmin // Explicitly pass the isAdmin flag
        }
      });

      // Immediately fetch full profile data after login to ensure avatar is loaded
      dispatch(fetchUserProfile());

      // Close the modal after successful login
      if (params.onClose) params.onClose();
      
      // Use navigation callback instead of page reload
      if (params.onSuccess) params.onSuccess();

      return { success: true };
    } else {
      throw new Error("Login failed - invalid response");
    }
  } catch (error) {
    console.error("Login error:", error);

    // Handle login failure but remove locking mechanism
    // Get current attempts
    let attempts = 0;
    const lockData = localStorage.getItem("loginLock");
    if (lockData) {
      const data = JSON.parse(lockData);
      attempts = data.attempts || 0;
    }

    // Increment attempts
    attempts++;

    // Just track the attempts but don't lock
    localStorage.setItem(
      "loginLock",
      JSON.stringify({
        attempts,
      })
    );

    dispatch({
      type: LOGIN_TYPES.LOGIN_ATTEMPT_FAILED,
      payload: { attempts },
    });

    return { success: false, attempts };
  }
};

export const validtoken = (token) => async (dispatch) => {
  try {
    const response = await apiClient.post('/user/validete', "", {
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
      });
    console.log(response);
    if (response.data === "Valid Token") {
      // Token is still valid
      dispatch(tokenisValid(true));

      return true;
    } else {
      // Token is invalid - logout
      dispatch(logout());

      return false;
    }
  } catch (error) {
    console.log(error);
    // Token validation failed
    dispatch(logout());

    return false;
  }
};

// New profile action creators
export const fetchUserProfile = () => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.FETCH_PROFILE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.get('/api/user/profile', {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

    if (response.data.success) {
      // Pre-cache avatar image so it's ready before any drawer/card renders
      if (response.data.profile?.userAvatar) {
        preloadAvatarImage(response.data.profile.userAvatar);
      }
      
      // Update localStorage with new profile data
      const currentUserData = JSON.parse(localStorage.getItem("burrowUserData") || "{}");
      const updatedUserData = {
        ...currentUserData,
        ...response.data.profile
      };
      localStorage.setItem("burrowUserData", JSON.stringify(updatedUserData));

      dispatch({
        type: LOGIN_TYPES.FETCH_PROFILE_SUCCESS,
        payload: {
          profile: response.data.profile
        }
      });
      
      return { success: true, profile: response.data.profile };
    } else {
      throw new Error(response.data.message || "Failed to fetch profile");
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    
    dispatch({
      type: LOGIN_TYPES.FETCH_PROFILE_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

export const updateUserBio = (bio) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.UPDATE_BIO_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.put('/api/user/profile/bio', { bio }, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

    if (response.data.success) {
      // Update localStorage with new bio
      const currentUserData = JSON.parse(localStorage.getItem("burrowUserData") || "{}");
      const updatedUserData = {
        ...currentUserData,
        userBio: response.data.bio
      };
      localStorage.setItem("burrowUserData", JSON.stringify(updatedUserData));

      dispatch({
        type: LOGIN_TYPES.UPDATE_BIO_SUCCESS,
        payload: {
          bio: response.data.bio
        }
      });
      
      return { success: true, bio: response.data.bio };
    } else {
      throw new Error(response.data.message || "Failed to update bio");
    }
  } catch (error) {
    console.error("Error updating user bio:", error);
    
    dispatch({
      type: LOGIN_TYPES.UPDATE_BIO_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

export const updateUserState = (userState) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.UPDATE_STATE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.put('/api/user/profile/state', { userState }, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (response.data.success) {
      // Update localStorage
      const currentUserData = JSON.parse(localStorage.getItem("burrowUserData") || "{}");
      const updatedUserData = {
        ...currentUserData,
        userState: response.data.userState
      };
      localStorage.setItem("burrowUserData", JSON.stringify(updatedUserData));

      dispatch({
        type: LOGIN_TYPES.UPDATE_STATE_SUCCESS,
        payload: {
          userState: response.data.userState
        }
      });
      
      return { success: true, userState: response.data.userState };
    } else {
      throw new Error(response.data.message || "Failed to update state");
    }
  } catch (error) {
    console.error("Error updating user state:", error);
    
    dispatch({
      type: LOGIN_TYPES.UPDATE_STATE_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

export const verifyOrderNumber = (orderNumber) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.VERIFY_ORDER_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.post('/api/user/verify-order', { orderNumber }, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

    if (response.data.success) {
      // Update localStorage with new credits
      const currentUserData = JSON.parse(localStorage.getItem("burrowUserData") || "{}");
      const updatedUserData = {
        ...currentUserData,
        accountCredits: response.data.credits
      };
      localStorage.setItem("burrowUserData", JSON.stringify(updatedUserData));

      dispatch({
        type: LOGIN_TYPES.VERIFY_ORDER_SUCCESS,
        payload: {
          credits: response.data.credits,
          added: response.data.added
        }
      });
      
      return { 
        success: true, 
        credits: response.data.credits,
        added: response.data.added 
      };
    } else {
      throw new Error(response.data.message || "Failed to verify order");
    }
  } catch (error) {
    console.error("Error verifying order:", error);
    
    dispatch({
      type: LOGIN_TYPES.VERIFY_ORDER_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

export const reportIssue = (category, description) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.REPORT_ISSUE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.post('/api/user/report-issue', { category, description }, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.REPORT_ISSUE_SUCCESS,
        payload: {
          reportId: response.data.reportId
        }
      });
      
      return { 
        success: true, 
        reportId: response.data.reportId 
      };
    } else {
      throw new Error(response.data.message || "Failed to report issue");
    }
  } catch (error) {
    console.error("Error reporting issue:", error);
    
    dispatch({
      type: LOGIN_TYPES.REPORT_ISSUE_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

export const deleteAccount = (password, confirmation) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.DELETE_ACCOUNT_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.delete('/api/user/account', {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        data: { password, confirmation }
      });

    if (response.data.success) {
      // Account deleted, log out the user
      dispatch(logout());
      
      return { success: true };
    } else {
      throw new Error(response.data.message || "Failed to delete account");
    }
  } catch (error) {
    console.error("Error deleting account:", error);
    
    dispatch({
      type: LOGIN_TYPES.DELETE_ACCOUNT_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// Message-related actions

// Fetch user's message inbox
export const fetchUserInbox = () => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.FETCH_MESSAGES_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    console.time('fetch-user-inbox');
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/api/user/messages/inbox`,
      {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      }
    );
    console.timeEnd('fetch-user-inbox');

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.FETCH_MESSAGES_SUCCESS,
        payload: response.data.inbox
      });
      
      return { success: true, inbox: response.data.inbox };
    } else {
      throw new Error(response.data.message || "Failed to fetch messages");
    }
  } catch (error) {
    console.error("Error fetching messages:", error);
    
    dispatch({
      type: LOGIN_TYPES.FETCH_MESSAGES_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// New action: Fetch user's sent messages
export const fetchSentMessages = () => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.FETCH_SENT_MESSAGES_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    console.time('fetch-sent-messages');
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/api/user/messages/sent-messages`,
      {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      }
    );
    console.timeEnd('fetch-sent-messages');

    // Fix the dispatch to use the correct response structure
    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.FETCH_SENT_MESSAGES_SUCCESS,
        payload: {
          messages: response.data.messages || [],
          total: response.data.total || 0
        }
      });
      
      return { success: true, sentMessages: response.data.messages };
    } else {
      throw new Error(response.data.message || "Failed to fetch sent messages");
    }
  } catch (error) {
    console.error("Error fetching sent messages:", error);
    
    dispatch({
      type: LOGIN_TYPES.FETCH_SENT_MESSAGES_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Send a message to another user
export const sendUserMessage = (recipientId, message) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.SEND_MESSAGE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.post(
      `${process.env.REACT_APP_URL}/api/user/messages/send-user-message`,
      { recipientId, message },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.SEND_MESSAGE_SUCCESS,
        payload: {
          recipientId,
          messageId: response.data.messageId
        }
      });
      
      return { success: true, messageId: response.data.messageId };
    } else {
      throw new Error(response.data.message || "Failed to send message");
    }
  } catch (error) {
    console.error("Error sending message:", error);
    
    // Phase 6: Surface server-side messaging gate messages (403 blocked/privacy)
    const serverMsg = error.response?.data?.message || error.response?.data?.error || error.message;
    
    dispatch({
      type: LOGIN_TYPES.SEND_MESSAGE_FAILURE,
      payload: {
        error: serverMsg
      }
    });
    
    return { 
      success: false, 
      error: serverMsg 
    };
  }
};

// Admin sends a message to a user
export const sendAdminMessage = (recipientId, message) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.SEND_MESSAGE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.post(
      `${process.env.REACT_APP_URL}/api/admin/messages/send-admin-message`,
      { recipientId, message },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.SEND_MESSAGE_SUCCESS,
        payload: {
          recipientId,
          messageId: response.data.messageId,
          isAdmin: true
        }
      });
      
      return { success: true, messageId: response.data.messageId };
    } else {
      throw new Error(response.data.message || "Failed to send admin message");
    }
  } catch (error) {
    console.error("Error sending admin message:", error);
    
    dispatch({
      type: LOGIN_TYPES.SEND_MESSAGE_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Mark messages as read
export const markMessagesAsRead = (messageIds, messageType) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.MARK_MESSAGE_READ_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.put(
      `${process.env.REACT_APP_URL}/api/user/messages/mark-as-read`,
      { messageIds, messageType },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.MARK_MESSAGE_READ_SUCCESS,
        payload: {
          messageIds,
          messageType,
          updateCount: response.data.updateCount
        }
      });
      
      return { success: true, updateCount: response.data.updateCount };
    } else {
      throw new Error(response.data.message || "Failed to mark messages as read");
    }
  } catch (error) {
    console.error("Error marking messages as read:", error);
    
    dispatch({
      type: LOGIN_TYPES.MARK_MESSAGE_READ_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Delete a message
export const deleteMessage = (messageId, messageType) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.DELETE_MESSAGE_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_URL}/api/user/messages/${messageId}`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        data: { messageType } // Send messageType in request body
      }
    );

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.DELETE_MESSAGE_SUCCESS,
        payload: {
          messageId,
          messageType
        }
      });
      
      return { success: true, messageId };
    } else {
      throw new Error(response.data.message || "Failed to delete message");
    }
  } catch (error) {
    console.error("Error deleting message:", error);
    
    dispatch({
      type: LOGIN_TYPES.DELETE_MESSAGE_FAILURE,
      payload: {
        error: error.message
      }
    });
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Avatar actions
export const fetchAvailableAvatars = () => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.FETCH_AVATARS_REQUEST });
    console.time('fetch_avatars');
    
    // Get user role from state
    const { auth } = getState();
    const isAdmin = auth?.user?.role === 'admin';
    
    // Use admin-specific endpoint for admin users
    const endpoint = isAdmin ? '/api/admin/get-available-avatars' : '/api/user/get-available-avatars';
    console.log(`Using ${isAdmin ? 'admin' : 'user'} avatar endpoint`);
    
    const response = await apiClient.get(endpoint);
    
    console.timeEnd('fetch_avatars');
    console.log(`Received ${response.data.tiers.length} avatar tiers, ${response.data.unlockedTiers.length} unlocked`);
    
    dispatch({
      type: LOGIN_TYPES.FETCH_AVATARS_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    console.error('Error fetching avatars:', error);
    dispatch({
      type: LOGIN_TYPES.FETCH_AVATARS_FAILURE,
      payload: error.response?.data?.message || 'Failed to load avatars'
    });
  }
};

export const updateUserAvatar = (avatarUrl) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.UPDATE_AVATAR_REQUEST });
    console.log('Updating user avatar to:', avatarUrl);
    
    const response = await apiClient.put('/api/user/profile/update-avatar', { avatarUrl });
    
    if (response?.data?.success) {
      // Pre-cache the new avatar image immediately
      preloadAvatarImage(avatarUrl);
      
      // Update localStorage with new avatar
      const userData = JSON.parse(localStorage.getItem('burrowUserData') || '{}');
      userData.userAvatar = avatarUrl;
      localStorage.setItem('burrowUserData', JSON.stringify(userData));
      
      console.log('Avatar updated successfully');
      
      dispatch({
        type: LOGIN_TYPES.UPDATE_AVATAR_SUCCESS,
        payload: response.data
      });
      
      // Refresh user profile to get updated data
      dispatch(fetchUserProfile());
      
      return true; // Return success flag for component
    } else {
      // Handle unexpected response structure
      throw new Error(response?.data?.message || 'Unexpected server response');
    }
  } catch (error) {
    console.error('Error updating avatar:', error);
    dispatch({
      type: LOGIN_TYPES.UPDATE_AVATAR_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to update avatar'
    });
    return false; // Return failure flag for component
  }
};

// Upload custom VIP profile image (one per user, overwrites previous)
export const uploadCustomAvatar = (file) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_TYPES.UPDATE_AVATAR_REQUEST });
    console.log('Uploading custom profile image:', file.name);
    
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await apiClient.post('/api/user/profile/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    if (response?.data?.success) {
      const avatarUrl = response.data.userAvatar;
      
      // Pre-cache the new avatar
      preloadAvatarImage(avatarUrl);
      
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem('burrowUserData') || '{}');
      userData.userAvatar = avatarUrl;
      localStorage.setItem('burrowUserData', JSON.stringify(userData));
      
      console.log('Profile image uploaded:', avatarUrl);
      
      dispatch({
        type: LOGIN_TYPES.UPDATE_AVATAR_SUCCESS,
        payload: response.data
      });
      
      // Refresh profile
      dispatch(fetchUserProfile());
      
      return { success: true, avatarUrl };
    } else {
      throw new Error(response?.data?.message || 'Upload failed');
    }
  } catch (error) {
    console.error('Error uploading profile image:', error);
    const msg = error.response?.data?.message || error.message || 'Failed to upload profile image';
    dispatch({
      type: LOGIN_TYPES.UPDATE_AVATAR_FAILURE,
      payload: msg
    });
    return { success: false, error: msg };
  }
};

// Allot Member Units to a user (admin action)
export const allotMemberUnits = (recipientId, amount, reason) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.ALLOT_MU_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.post(
      '/api/admin/allot-mu',
      { recipientId, amount, reason }
    );

    if (response.data?.success) {
      dispatch({
        type: LOGIN_TYPES.ALLOT_MU_SUCCESS,
        payload: {
          newBalance: response.data.newBalance,
          transaction: response.data.transaction
        }
      });
      
      return { 
        success: true, 
        data: response.data 
      };
    } else {
      throw new Error(response.data?.message || "Failed to allot Member Units");
    }
  } catch (error) {
    console.error("Error allotting MU:", error);
    
    dispatch({
      type: LOGIN_TYPES.ALLOT_MU_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { 
      success: false, 
      error: error.response?.data?.message || error.message 
    };
  }
};

// Gift Member Units to another user
export const giftMemberUnits = (recipientId, amount) => async (dispatch, getState) => {
  try {
    dispatch({ type: LOGIN_TYPES.GIFT_MU_REQUEST });

    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response = await apiClient.post(
      '/api/user/gift-mu',
      { recipientId, amount }
    );

    if (response.data?.success) {
      dispatch({
        type: LOGIN_TYPES.GIFT_MU_SUCCESS,
        payload: {
          newBalance: response.data.newBalance,
          transaction: response.data.transaction
        }
      });

      // Update profile with new balance
      dispatch(fetchUserProfile());

      return {
        success: true,
        data: response.data
      };
    } else {
      throw new Error(response.data?.message || "Failed to gift Member Units");
    }
  } catch (error) {
    console.error("Error gifting MU:", error);

    dispatch({
      type: LOGIN_TYPES.GIFT_MU_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });

    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};
// Backward-compatible alias
export const giftCredits = giftMemberUnits;

// Chat Message Actions
export const fetchChatMessages = () => async (dispatch) => {
  try {
    dispatch({ type: 'FETCH_CHAT_MESSAGES_REQUEST' });
    
    const response = await apiClient.get('/api/chat/messages');
    
    if (response.data.success) {
      dispatch({
        type: 'FETCH_CHAT_MESSAGES_SUCCESS',
        payload: response.data.messages
      });
      return { success: true, messages: response.data.messages };
    } else {
      dispatch({
        type: 'FETCH_CHAT_MESSAGES_FAILURE',
        payload: response.data.message || 'Failed to fetch chat messages'
      });
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    dispatch({
      type: 'FETCH_CHAT_MESSAGES_FAILURE',
      payload: error.message || 'Failed to fetch chat messages'
    });
    return { success: false, message: error.message };
  }
};

export const sendChatMessage = (messageContent, messageType = 'chat', replyToId = 'false', chatType = 'global', state = 'false') => async (dispatch) => {
  try {
    dispatch({ type: 'SEND_CHAT_MESSAGE_REQUEST' });
    
    const messageData = {
      userChatMessageContents: messageContent,
      messageType,
      messageReplyToId: replyToId,
      messageChatType: chatType,
      messageState: state
    };
    
    const response = await apiClient.post('/api/chat/send', messageData);
    
    if (response.data.success) {
      dispatch({
        type: 'SEND_CHAT_MESSAGE_SUCCESS',
        payload: response.data.message
      });
      return { success: true, message: response.data.message };
    } else {
      dispatch({
        type: 'SEND_CHAT_MESSAGE_FAILURE',
        payload: response.data.message || 'Failed to send message'
      });
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    dispatch({
      type: 'SEND_CHAT_MESSAGE_FAILURE',
      payload: error.message || 'Failed to send message'
    });
    return { success: false, message: error.message };
  }
};

// ─── Phase 1C: Admin Elevation & Switch-Back Actions ───────────────────────────

/**
 * Elevate to admin mode. Requires user to provide admin password.
 * Calls POST /api/admin/elevate with the current user JWT.
 * Stores admin JWT in Redux (NOT in localStorage — admin mode is session-only).
 */
export const elevateToAdmin = (password) => async (dispatch, getState) => {
  try {
    const token = getState().auth.token || localStorage.getItem('burrowtoken');
    const response = await apiClient.post('/api/admin/elevate', 
      { password },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data.success) {
      dispatch({
        type: LOGIN_TYPES.ADMIN_ELEVATE_SUCCESS,
        payload: {
          adminToken: response.data.adminToken,
          admin: response.data.admin
        }
      });
      return { success: true };
    } else {
      return { success: false, message: response.data.message || 'Elevation failed' };
    }
  } catch (error) {
    const msg = error.response?.data?.message || error.message || 'Failed to elevate to admin';
    return { success: false, message: msg };
  }
};

/**
 * Switch back from admin mode to user mode.
 * Calls POST /api/admin/switch-to-user with the admin JWT.
 * Clears admin state, optionally refreshes user JWT.
 */
export const switchToUserMode = () => async (dispatch, getState) => {
  try {
    const adminToken = getState().auth.adminToken;
    if (!adminToken) {
      // No admin token — just clear state
      dispatch({ type: LOGIN_TYPES.ADMIN_SWITCH_TO_USER, payload: {} });
      return { success: true };
    }

    const response = await apiClient.post('/api/admin/switch-to-user',
      {},
      { headers: { Authorization: `Bearer ${adminToken}` } }
    );

    if (response.data.success) {
      // Update user token if server issued a fresh one
      if (response.data.token) {
        localStorage.setItem('burrowtoken', response.data.token);
        localStorage.setItem('burrowUserData', JSON.stringify(response.data.user));
      }
      dispatch({
        type: LOGIN_TYPES.ADMIN_SWITCH_TO_USER,
        payload: {
          token: response.data.token,
          user: response.data.user
        }
      });
      return { success: true };
    } else {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    // Even if the call fails, clear admin state locally
    dispatch({ type: LOGIN_TYPES.ADMIN_SWITCH_TO_USER, payload: {} });
    return { success: false, message: error.message };
  }
};

// ─── Phase 6: Privacy Settings Actions ───────────────────────────────────────

export const getPrivacySettings = () => async (dispatch, getState) => {
  try {
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) throw new Error("Authentication token not found");

    const response = await apiClient.get('/api/user/privacy-settings', {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success) {
      return { success: true, privacySettings: response.data.privacySettings, blockedUsers: response.data.blockedUsers };
    }
    throw new Error(response.data.message || "Failed to load privacy settings");
  } catch (error) {
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

export const updatePrivacySettings = (settings) => async (dispatch, getState) => {
  try {
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) throw new Error("Authentication token not found");

    const response = await apiClient.put('/api/user/privacy-settings', settings, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    });

    if (response.data.success) {
      // Update local profile data with new privacy settings
      const currentUserData = JSON.parse(localStorage.getItem("burrowUserData") || "{}");
      currentUserData.privacySettings = response.data.privacySettings;
      localStorage.setItem("burrowUserData", JSON.stringify(currentUserData));

      dispatch({
        type: LOGIN_TYPES.UPDATE_BIO_SUCCESS, // Reuse profile update action type
        payload: { privacySettings: response.data.privacySettings }
      });

      return { success: true, privacySettings: response.data.privacySettings };
    }
    throw new Error(response.data.message || "Failed to update privacy settings");
  } catch (error) {
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

export const blockUser = (burrowerId) => async (dispatch, getState) => {
  try {
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) throw new Error("Authentication token not found");

    const response = await apiClient.post(`/api/user/block/${burrowerId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success) {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to block user");
  } catch (error) {
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

export const unblockUser = (burrowerId) => async (dispatch, getState) => {
  try {
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) throw new Error("Authentication token not found");

    const response = await apiClient.delete(`/api/user/block/${burrowerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success) {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to unblock user");
  } catch (error) {
    return { success: false, error: error.response?.data?.message || error.message };
  }
};